import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  ONLINE_DEPOSIT_STATUS_OPTIONS,
  buildOnlineDepositPageParams,
  defaultOnlineDepositApplyTimeRange,
  depositStatusClassName,
  formatAmount,
  formatDepositStatus,
  formatOptionalId,
  validateOnlineDepositTimeRange,
} from './onlineDepositConstants';

const DEFAULT_RANGE = defaultOnlineDepositApplyTimeRange();

const EMPTY_FILTERS = {
  id: '',
  thirdRecordCode: '',
  status: '',
  callbackStatus: '',
  roleId: '',
  topInviterId: '',
  inviterId: '',
  memberId: '',
  phoneEmail: '',
  currencyName: '',
  address: '',
  createTimeFromInput: DEFAULT_RANGE.createTimeFromInput,
  createTimeToInput: DEFAULT_RANGE.createTimeToInput,
  updateTimeFromInput: '',
  updateTimeToInput: '',
  callbackTimeFromInput: '',
  callbackTimeToInput: '',
};

const TABLE_COLUMNS = [
  '頂級上級用戶ID',
  '充值單號',
  '商戶 Code',
  '三方單號',
  '主狀態',
  '回調狀態',
  '帳號類型',
  '直屬上級用戶ID',
  '用戶ID',
  '幣種',
  '充值數量',
  '實際入款',
  '申請時間',
  '回調時間',
  '更新時間',
  '備註',
  '操作',
];

const EMPTY_TOTAL = {
  recordCount: 0,
  amount: 0,
  realAmount: 0,
};

function statusCode(value) {
  if (!value) {
    return '';
  }
  return typeof value === 'string' ? value : value?.name || value?.code || String(value);
}

export default function OnlineDepositListPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(EMPTY_TOTAL);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [modifyTarget, setModifyTarget] = useState(null);
  const [callbackTarget, setCallbackTarget] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    const params = buildOnlineDepositPageParams(pageNo, nextFilters);
    try {
      const [pageData, totalData] = await Promise.all([
        adminApi.onlineDepositPage(params),
        adminApi.onlineDepositTotal(params),
      ]);
      setRecords(pageData?.records || []);
      setPage(pageData?.current || pageNo);
      setTotalPages(pageData?.pages || 1);
      setTotal(totalData || EMPTY_TOTAL);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
      setTotal(EMPTY_TOTAL);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function validateFilters() {
    const applyError = validateOnlineDepositTimeRange(
      filters.createTimeFromInput,
      filters.createTimeToInput,
      '申請時間'
    );
    if (applyError) {
      return applyError;
    }
    const updateError = validateOnlineDepositTimeRange(
      filters.updateTimeFromInput,
      filters.updateTimeToInput,
      '更新時間'
    );
    if (updateError) {
      return updateError;
    }
    return validateOnlineDepositTimeRange(
      filters.callbackTimeFromInput,
      filters.callbackTimeToInput,
      '回調時間'
    );
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateFilters();
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setError('');
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateFilters();
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      await adminApi.exportOnlineDepositCsvZip({
        ...buildOnlineDepositPageParams(page, filters),
        exportCurrentPageOnly,
        fileName: 'member_online_deposit',
      });
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function confirmModifyStatus() {
    const row = modifyTarget;
    if (!row?.id) {
      return;
    }
    setActionId(row.id);
    setError('');
    try {
      await adminApi.onlineDepositUpdateToProcessing({ id: row.id });
      setModifyTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '修改狀態失敗');
    } finally {
      setActionId(null);
    }
  }

  async function confirmManualCallback() {
    const row = callbackTarget;
    if (!row?.id) {
      return;
    }
    setActionId(row.id);
    setError('');
    try {
      await adminApi.onlineDepositCheckOrder({
        id: row.id,
        merchantCode: row.merchantCode?.trim() || row.address?.trim() || undefined,
      });
      setCallbackTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '手動回調失敗');
    } finally {
      setActionId(null);
    }
  }

  function canModifyStatus(row) {
    const main = statusCode(row.status);
    const cb = statusCode(row.callbackStatus);
    if (main === 'PROCESSING' || main === 'SUCCESS' || main === 'FAIL') {
      return false;
    }
    return main === 'WAITING_CALLBACK' || main === 'TIMEOUT' || cb === 'TIMEOUT';
  }

  function canManualCallback(row) {
    return statusCode(row.status) === 'PROCESSING';
  }

  return (
    <div className="page-card">
      <h2>線上充值列表</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="odId">充值單號</label>
          <input
            id="odId"
            type="number"
            value={filters.id}
            onChange={(e) => updateFilter('id', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odThirdRecordCode">三方單號</label>
          <input
            id="odThirdRecordCode"
            type="text"
            value={filters.thirdRecordCode}
            onChange={(e) => updateFilter('thirdRecordCode', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odStatus">主狀態</label>
          <select
            id="odStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="">全部</option>
            {ONLINE_DEPOSIT_STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="odCallbackStatus">回調狀態</label>
          <select
            id="odCallbackStatus"
            value={filters.callbackStatus}
            onChange={(e) => updateFilter('callbackStatus', e.target.value)}
          >
            <option value="">全部</option>
            {ONLINE_DEPOSIT_STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="odRoleId">帳號類型</label>
          <select
            id="odRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="odTopInviterId">頂級上級用戶ID</label>
          <input
            id="odTopInviterId"
            type="number"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odInviterId">直屬上級用戶ID</label>
          <input
            id="odInviterId"
            type="number"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odMemberId">用戶ID</label>
          <input
            id="odMemberId"
            type="number"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odPhoneEmail">郵箱/手機</label>
          <input
            id="odPhoneEmail"
            type="text"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odCurrencyName">幣種</label>
          <input
            id="odCurrencyName"
            type="text"
            value={filters.currencyName}
            onChange={(e) => updateFilter('currencyName', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odAddress">充值地址</label>
          <input
            id="odAddress"
            type="text"
            value={filters.address}
            onChange={(e) => updateFilter('address', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odCreateTimeFrom">申請時間起</label>
          <input
            id="odCreateTimeFrom"
            type="datetime-local"
            value={filters.createTimeFromInput}
            onChange={(e) => updateFilter('createTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odCreateTimeTo">申請時間迄</label>
          <input
            id="odCreateTimeTo"
            type="datetime-local"
            value={filters.createTimeToInput}
            onChange={(e) => updateFilter('createTimeToInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odUpdateTimeFrom">更新時間起</label>
          <input
            id="odUpdateTimeFrom"
            type="datetime-local"
            value={filters.updateTimeFromInput}
            onChange={(e) => updateFilter('updateTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odUpdateTimeTo">更新時間迄</label>
          <input
            id="odUpdateTimeTo"
            type="datetime-local"
            value={filters.updateTimeToInput}
            onChange={(e) => updateFilter('updateTimeToInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odCallbackTimeFrom">回調時間起</label>
          <input
            id="odCallbackTimeFrom"
            type="datetime-local"
            value={filters.callbackTimeFromInput}
            onChange={(e) => updateFilter('callbackTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="odCallbackTimeTo">回調時間迄</label>
          <input
            id="odCallbackTimeTo"
            type="datetime-local"
            value={filters.callbackTimeToInput}
            onChange={(e) => updateFilter('callbackTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const mainStatus = statusCode(row.status);
                const cbStatus = statusCode(row.callbackStatus);

                return (
                  <tr key={row.id}>
                    <td>{formatOptionalId(row.topInviterId)}</td>
                    <td className="mono-cell">{formatOptionalId(row.id)}</td>
                    <td>{row.merchantCode || '—'}</td>
                    <td className="mono-cell">{row.thirdRecordCode || '—'}</td>
                    <td className={depositStatusClassName(mainStatus)}>
                      {formatDepositStatus(mainStatus)}
                    </td>
                    <td className={depositStatusClassName(cbStatus)}>
                      {formatDepositStatus(cbStatus)}
                    </td>
                    <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                    <td>{formatOptionalId(row.inviterId)}</td>
                    <td>{formatOptionalId(row.memberId)}</td>
                    <td>{row.currencyName || '—'}</td>
                    <td>{formatAmount(row.amount)}</td>
                    <td>{formatAmount(row.realAmount)}</td>
                    <td>{formatDateTime(row.createTime)}</td>
                    <td>{formatDateTime(row.callbackTime)}</td>
                    <td>{formatDateTime(row.updateTime)}</td>
                    <td className="remark-cell">{row.remark || '—'}</td>
                    <td>
                      <div className="table-actions table-actions--stack">
                        {canModifyStatus(row) && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={actionId === row.id}
                            onClick={() => setModifyTarget(row)}
                          >
                            {actionId === row.id ? '處理中…' : '修改狀態'}
                          </button>
                        )}
                        {canManualCallback(row) && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={actionId === row.id}
                            onClick={() => setCallbackTarget(row)}
                          >
                            {actionId === row.id ? '處理中…' : '手動回調'}
                          </button>
                        )}
                        {!canModifyStatus(row) && !canManualCallback(row) && '—'}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
            {!loading && records.length > 0 && (
              <tr className="summary-row">
                <td colSpan={10}>合計 {total.recordCount ?? 0} 筆</td>
                <td>{formatAmount(total.amount)}</td>
                <td>{formatAmount(total.realAmount)}</td>
                <td colSpan={4} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <ConfirmDialog
        open={Boolean(modifyTarget)}
        title="提示"
        message="是否要修改狀態為「處理中」"
        confirmLabel="確認"
        cancelLabel="取消"
        confirming={Boolean(modifyTarget && actionId === modifyTarget.id)}
        onClose={() => setModifyTarget(null)}
        onConfirm={confirmModifyStatus}
      />

      <ConfirmDialog
        open={Boolean(callbackTarget)}
        title="提示"
        message="確認手動請求回調？"
        confirmLabel="確認"
        cancelLabel="取消"
        confirming={Boolean(callbackTarget && actionId === callbackTarget.id)}
        onClose={() => setCallbackTarget(null)}
        onConfirm={confirmManualCallback}
      />
    </div>
  );
}
